import type {Map as ImmutableMap} from 'immutable';

import type {ClassroomModelV1} from '@albert-io/models';

/**
 * Scope (all-classes vs one class) + tab (sub-page) model shared by the sidemenu and class selector.
 * Switching scope keeps the current tab when the target has it, else falls back to the default
 * view: assignments, or People for an empty active class so the teacher lands invite-ready.
 */
export type NavScope = {kind: 'all'} | {kind: 'class'; classId: string};

export type NavTab =
  | 'assignments'
  | 'subjects'
  | 'diagnostics'
  | 'folders'
  | 'overview'
  | 'people'
  | 'settings';

const CLASS_PATH = /^\/classes\/([^/]+)/;

export const getNavScope = (pathname: string): NavScope => {
  const match = pathname.match(CLASS_PATH);
  // /classes and /classes/archived are the cross-class list, not a class scope.
  if (match && match[1] && match[1] !== 'archived') {
    return {kind: 'class', classId: match[1]};
  }
  return {kind: 'all'};
};

export const getNavTab = (pathname: string): NavTab | null => {
  if (pathname.includes('/people')) return 'people';
  if (pathname.includes('/settings')) return 'settings';
  if (pathname.includes('/students')) return 'overview';
  // /class-subjects is the cross-class view; /classes/:id/subjects is the per-class one.
  if (pathname.startsWith('/class-subjects') || pathname.includes('/subjects')) return 'subjects';
  if (pathname.includes('/diagnostics')) return 'diagnostics';
  if (
    pathname === '/saved' ||
    pathname.startsWith('/saved/') ||
    pathname === '/folders' ||
    pathname.startsWith('/folders/')
  ) {
    return 'folders';
  }
  // Last so /my-assignments (no "/assignments" substring) and the above win first.
  if (pathname.includes('/assignments')) return 'assignments';
  return null;
};

/**
 * Scope-neutral routes have no per-class equivalent, so opening one must not reset the remembered
 * class scope: Dashboard (/home) and Folders. Explicit cross-class routes (/assignments,
 * /class-subjects, /diagnostics) are NOT scope-neutral — navigating to them selects the "all" scope.
 */
export const isScopeNeutralRoute = (pathname: string): boolean =>
  pathname === '/home' || getNavTab(pathname) === 'folders';

const ALL_ROUTES: Partial<Record<NavTab, string>> = {
  assignments: '/assignments',
  subjects: '/class-subjects',
  diagnostics: '/diagnostics',
  folders: '/folders'
};

const getClassRoutes = (classId: string): Partial<Record<NavTab, string>> => ({
  overview: `/classes/${classId}/students/overview`,
  assignments: `/classes/${classId}/assignments`,
  subjects: `/classes/${classId}/subjects`,
  people: `/classes/${classId}/people`,
  settings: `/classes/${classId}/settings/details`
});

/**
 * No enrolled students AND no pending invitations, on an active class. The counts ride the
 * `classroom_counts` meta context (see getTeacherSidemenuClassroomsQuery); if the meta is absent
 * the check fails open to false, keeping the Assignments default.
 */
export const isEmptyActiveClass = (classroom: ClassroomModelV1 | undefined): boolean => {
  // typeof guard: a mid-refetch interest can hand back a raw stub without model methods.
  if (
    !classroom ||
    typeof classroom.getStatus !== 'function' ||
    classroom.getStatus() !== 'active'
  ) {
    return false;
  }
  // Raw meta reads — the generated getters default missing counts to 0, which would misread a
  // meta-less classroom as empty.
  const meta = (classroom as unknown as ImmutableMap<string, unknown>).get('meta') as
    | ImmutableMap<string, unknown>
    | undefined;
  return (
    meta?.get('count_of_students') === 0 &&
    meta?.get('count_of_pending_classroom_invitations') === 0
  );
};

export const resolveScopeRoute = (
  target: NavScope,
  currentTab: NavTab | null,
  {emptyActiveClass = false}: {emptyActiveClass?: boolean} = {}
): string => {
  const routes = target.kind === 'all' ? ALL_ROUTES : getClassRoutes(target.classId);
  if (currentTab && routes[currentTab]) {
    return routes[currentTab];
  }
  return target.kind === 'class' && emptyActiveClass ? routes.people! : routes.assignments!;
};

/** A just-created class is active and empty by definition, so it opens on People. */
export const getCreatedClassRoute = (classId: string): string =>
  resolveScopeRoute({kind: 'class', classId}, null, {emptyActiveClass: true});
